import React, { useState, useEffect, useRef } from "react";
import { Mail, Send, Clock, ArrowUpRight } from "lucide-react";
import { LINKS, ASSETS } from "../lib/constants";

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

const SectionLabel = ({ children }) => (
  <span className="section-label">{children}</span>
);

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", business: "", interest: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const tidycalRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const initialized = { done: false };
    const tryInit = () => {
      if (cancelled || initialized.done) return;
      const el = tidycalRef.current;
      if (window.TidyCal && el && el.isConnected && el.tagName === "DIV" && el.classList.contains("tidycal-embed")) {
        initialized.done = true;
        try { window.TidyCal.init(el); } catch (_err) { /* ignore */ }
        return;
      }
      attempts += 1;
      if (attempts < 40) setTimeout(tryInit, 150);
    };
    tryInit();
    return () => { cancelled = true; };
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!WEB3FORMS_KEY) {
      window.location.href = `mailto:${LINKS.email}?subject=Enquiry from ${encodeURIComponent(form.name)}&body=${encodeURIComponent(form.message)}`;
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `New enquiry from ${form.name || "ProfitMIND website"}${form.interest ? ` — ${form.interest}` : ""}`,
          from_name: form.name,
          name: form.name,
          email: form.email,
          business: form.business,
          interest: form.interest,
          message: form.message,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus("success");
        setForm({ name: "", email: "", business: "", interest: "", message: "" });
      } else {
        setStatus("error");
        setErrorMsg(data.message || "Please try again, or email hello@profitmind.co.uk directly.");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Please try again, or email hello@profitmind.co.uk directly.");
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="bg-white pt-12 pb-12 lg:pt-16 lg:pb-12">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 reveal text-center">
          <SectionLabel>Contact</SectionLabel>
          <h1 className="font-display text-[clamp(2.4rem,5.6vw,4.6rem)] mt-6 leading-[0.95]">
            Let's talk about where you're <span className="hi-yellow">stuck in your cash journey.</span>
          </h1>
          <p className="mt-7 text-lg sm:text-xl leading-relaxed text-brand-ink mx-auto">
            Whether you're curious about forecastly, wondering if the Advisory Programme is the
            right fit, interested in a Money Flow Strategy Session, or inviting me to speak on your
            podcast or to your community — you can get in touch here.
          </p>
        </div>
      </section>

      {/* TIDYCAL + PHOTO */}
      <section className="bg-white py-12 border-y-[1.5px] border-brand-black/15" data-testid="contact-tidycal-section">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <span className="text-xs uppercase tracking-[0.18em] text-brand-stone">For a quick clarity conversation</span>
              <h2 className="font-display text-3xl sm:text-4xl mt-2">Book a free 20-min chat</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-brand-ink max-w-2xl">
                We'll talk through where you are, what feels heavy, and which part of Follow The
                Cash™ would make the most difference right now. 20 minutes. No obligation.
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-brand-stone">
              <Clock size={16} className="text-brand-yellow" /> Zoom or phone
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4 order-2 lg:order-1 flex flex-col">
              <div className="relative aspect-[4/5]">
                <div className="absolute -bottom-3 -right-3 w-full h-full bg-brand-yellow rounded-[10px] -z-0" aria-hidden="true" />
                <div className="relative h-full w-full rounded-[10px] overflow-hidden border-[3px] border-brand-black z-10">
                  <img src={ASSETS.portraitLaughingStripes} alt="Magdalena Mahdy — ProfitMIND founder" className="w-full h-full object-cover block" width="800" height="1000" data-testid="contact-portrait" />
                </div>
              </div>
              <div className="mt-6 bg-brand-black text-white rounded-[10px] p-5">
                <p className="font-display text-xl leading-tight uppercase">No pitch. No pressure.</p>
                <p className="text-sm text-white/80 mt-2 leading-relaxed">
                  Just 20 minutes to talk about your business, your numbers and what calm money
                  decisions could look like for you.
                </p>
              </div>
            </div>

            <div className="lg:col-span-8 order-1 lg:order-2">
              <div className="rounded-[10px] overflow-hidden border-[1.5px] border-brand-black bg-white p-2 sm:p-4">
                <div ref={tidycalRef} className="tidycal-embed" data-path="profitmind/free-20min-chat" data-testid="tidycal-embed" />
              </div>
              <p className="text-xs text-brand-stone mt-4">
                Calendar not loading?{" "}
                <a href={LINKS.tidycal} target="_blank" rel="noopener noreferrer" className="underline decoration-brand-yellow decoration-2 underline-offset-2">
                  Open it in a new tab
                </a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OTHER REASONS */}
      <section className="bg-white py-16 lg:py-20" data-testid="contact-reasons">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-brand-yellow border-[1.5px] border-brand-black rounded-[10px] p-6 flex flex-col">
              <p className="text-xs uppercase tracking-[0.18em] text-brand-black/80 font-bold mb-3">For speaking, workshops or collaborations</p>
              <p className="text-[15px] leading-relaxed text-brand-black mb-3">
                I'm available for podcasts, panels, masterclasses and practical, plain-English
                workshops on:
              </p>
              <ul className="text-[14px] leading-relaxed text-brand-black space-y-1.5 mb-4">
                <li>· <em>Profitable But Broke: The Hidden Crisis Most Business Owners Won't Name</em> (keynote/talk)</li>
                <li>· <em>Follow The Cash™: A Four-Part Method for Financial Calm</em> (workshop)</li>
                <li>· <em>Why Profit Doesn't Equal Cash — And What To Do About It</em> (talk)</li>
                <li>· <em>Cashflow Forecasting Without the Spreadsheet Trauma</em> (hands-on workshop)</li>
              </ul>
              <a href={`mailto:${LINKS.email}?subject=Speaking%2FWorkshop%20enquiry`} className="inline-flex items-center gap-1 text-sm font-semibold mt-auto pt-2 text-brand-black hover:underline" data-testid="contact-speaking-cta">
                Send a speaking enquiry <ArrowUpRight size={15} />
              </a>
            </div>

            <div className="bg-white border-[1.5px] border-brand-black rounded-[10px] p-6 flex flex-col transition-all hover:-translate-y-1 hover:shadow-[6px_6px_0_#0A0A0A]">
              <p className="text-xs uppercase tracking-[0.18em] text-brand-stone font-bold mb-3">For forecastly</p>
              <p className="text-[15px] leading-relaxed text-brand-ink">
                If you're curious about the 'Take Control' tool I'm building, join the waitlist or
                send a message with your question.
              </p>
              <a href={LINKS.forecastly} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold mt-auto pt-5 text-brand-black hover:text-brand-yellow transition-colors" data-testid="contact-forecastly-cta">
                Join the forecastly waitlist <ArrowUpRight size={15} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="contact-form" className="bg-brand-stone-50 py-20 lg:py-24 border-y-[1.5px] border-brand-black/15" data-testid="contact-form-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <SectionLabel>General enquiry form</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl mt-4 leading-[1]">Send me a message.</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-brand-ink">
            For general enquiries, use the form below and I'll get back to you as soon as I can.
          </p>
          {status === "success" ? (
            <div className="mt-8 bg-brand-yellow border-[1.5px] border-brand-black rounded-[10px] p-8 text-center" data-testid="contact-success">
              <Mail size={40} className="mx-auto text-brand-black mb-3" />
              <h3 className="font-display text-3xl mb-2">Thank you!</h3>
              <p className="text-brand-black/85">Your message has landed in my inbox. I'll be in touch within 2 working days.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-5 bg-white border-[1.5px] border-brand-black rounded-[10px] p-7 text-left" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-xs uppercase tracking-[0.16em] font-bold text-brand-stone mb-1.5">Name</label>
                  <input id="contact-name" name="name" required value={form.name} onChange={handleChange} className="w-full px-4 py-3 border-[1.5px] border-brand-black rounded-md font-sans text-sm focus:outline-none focus:ring-[3px] focus:ring-brand-yellow" data-testid="contact-input-name" />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs uppercase tracking-[0.16em] font-bold text-brand-stone mb-1.5">Email</label>
                  <input id="contact-email" name="email" type="email" required value={form.email} onChange={handleChange} className="w-full px-4 py-3 border-[1.5px] border-brand-black rounded-md font-sans text-sm focus:outline-none focus:ring-[3px] focus:ring-brand-yellow" data-testid="contact-input-email" />
                </div>
              </div>
              <div>
                <label htmlFor="contact-business" className="block text-xs uppercase tracking-[0.16em] font-bold text-brand-stone mb-1.5">Business (optional)</label>
                <input id="contact-business" name="business" value={form.business} onChange={handleChange} className="w-full px-4 py-3 border-[1.5px] border-brand-black rounded-md font-sans text-sm focus:outline-none focus:ring-[3px] focus:ring-brand-yellow" data-testid="contact-input-business" />
              </div>
              <div>
                <label htmlFor="contact-interest" className="block text-xs uppercase tracking-[0.16em] font-bold text-brand-stone mb-1.5">I'm interested in…</label>
                <select id="contact-interest" name="interest" value={form.interest} onChange={handleChange} className="w-full px-4 py-3 border-[1.5px] border-brand-black rounded-md font-sans text-sm bg-white focus:outline-none focus:ring-[3px] focus:ring-brand-yellow" data-testid="contact-input-interest">
                  <option value="">Select an option…</option>
                  <option value="Forecastly">Forecastly</option>
                  <option value="Money Flow Session">Money Flow Session</option>
                  <option value="Advisory Programme">Advisory Programme</option>
                  <option value="Speaking & Workshops">Speaking &amp; Workshops</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-xs uppercase tracking-[0.16em] font-bold text-brand-stone mb-1.5">Message</label>
                <textarea id="contact-message" name="message" rows={6} required value={form.message} onChange={handleChange} className="w-full px-4 py-3 border-[1.5px] border-brand-black rounded-md font-sans text-sm focus:outline-none focus:ring-[3px] focus:ring-brand-yellow resize-none" data-testid="contact-input-message" />
              </div>
              <button type="submit" disabled={status === "sending"} className="btn-yellow disabled:opacity-60" data-testid="contact-submit-btn">
                {status === "sending" ? "Sending…" : (<>Send message <Send size={16} /></>)}
              </button>
              {status === "error" && (
                <p className="text-xs text-red-600" data-testid="contact-error">{errorMsg}</p>
              )}
              <p className="text-[11px] text-brand-stone leading-relaxed">
                Submissions are processed by Web3Forms and emailed directly to me. Your information is never stored
                or used for marketing. See our <a href="/privacy-policy" className="underline decoration-brand-yellow decoration-2 underline-offset-2">Privacy Policy</a>.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-yellow border-y-[1.5px] border-brand-black" data-testid="contact-final-cta">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h3 className="font-display text-3xl sm:text-4xl uppercase max-w-3xl leading-tight text-brand-black">
            Ready to stop being Profitable But Broke?
          </h3>
          <a href={LINKS.freeAssessment} target="_blank" rel="noopener noreferrer" data-testid="contact-final-reality-btn" className="btn-primary shadow-w-default shrink-0">
            Take the free Profitable-But-Broke Reality Check <ArrowUpRight size={18} />
          </a>
        </div>
      </section>
    </div>
  );
}
